import React, { useEffect, useState } from 'react';
import AppFile from '../../models/AppFile';
import { Button, CircularProgress, Grid, Typography } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDoneIcon from '@mui/icons-material/CloudDone';
import useFileUploader from '../../shared/hooks/useFileUploader';
import { useTranslation } from 'react-i18next';
import { megabytesToBytes } from '../../shared/utils/commons';
import './ButtonFileUploader.css'
import { FileType } from '../../models/FileType';

export interface ButtonFileUploaderProps {
  id?: string;
  defaultFile: AppFile | null;
  label: string;
  inputFileDocumentName: string;
  documentType: FileType;
  documentAllowedSize: string;
  validation?: React.ReactNode
  onFileUploded: (file: AppFile | null) => void;
  disabled?: boolean;
  buttonStyle?: React.CSSProperties;
  gridDirectionRow?: boolean;
}

const ButtonFileUploader = ({ id = 'upload-syllabusFile', defaultFile, label, inputFileDocumentName, documentType, documentAllowedSize, validation, onFileUploded, disabled, buttonStyle, gridDirectionRow }: ButtonFileUploaderProps) => {
  const { t } = useTranslation();
  const uploader = useFileUploader(defaultFile, documentType);
  const [isInvalid, setIsInvalid] = useState<boolean>(false);
  const [invalidMessage, setInvalidMessage] = useState<string>('');
  const [firstOpen, setFirstOpen] = useState<boolean>(true);

  const setInvalidFile = (message: string) => {
    setInvalidMessage(message);
    setIsInvalid(true);
  }

  const setValidFile = () => {
    setIsInvalid(false);
    setInvalidMessage('');
  }

  useEffect(() => {
    // This logic help to mantain the file when the user goes closes the modal and open it again
    if (firstOpen) {
      setFirstOpen(false);
      if (defaultFile) {
        onFileUploded(defaultFile)
      } else {
        onFileUploded(uploader?.appFile)
      }
    } else {
      onFileUploded(uploader?.appFile)
    }
  }, [uploader?.appFile, firstOpen]);

  const handlerOnFileUploaded = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target?.files;
    if (files !== null && files.length > 0 && isValidFile(files)) {
      uploader?.changeFile(files[0]);
    } else {
      uploader?.changeFile(null);
    }
  }

  const isValidFile = (files: FileList): boolean => {
    const fileToUpload = files[0];
    const fileExtension = fileToUpload.name.split('.').pop()?.toLowerCase();
    if (fileExtension && !getAllowedExtensions().allowed.includes(fileExtension)) {
      setInvalidFile(t('invalidFileType'));
      return false;
    }
    if (fileToUpload && fileToUpload.size >= megabytesToBytes(documentAllowedSize)) {
      setInvalidFile(t('invalidFileSize'));
      return false;
    }
    setValidFile();
    return true;
  }

  const getAllowedExtensions = () => {
    if (documentType === 'DU_FRONT' || documentType === 'DU_BACK') {
      return {
        allowed: ['jpg', 'jpeg', 'png'],
        accept: 'image/*'
      };
    }
    return {
      allowed: ['pdf', 'doc', 'docx'],
      accept: 'application/pdf'
    };
  }

  return (
    <Grid className="REQXX-ButtonFileUploader-container" container direction={'row'} alignItems={'center'}>
      <Grid item direction={gridDirectionRow ? 'row' : 'column'} className="REQXX-ButtonFileUploader-btncontainer" sx={gridDirectionRow ? { display: 'flex' } : {}}>
        <Button
          disabled={disabled}
          onClick={e => e.stopPropagation()}
          onKeyDown={(e: React.KeyboardEvent) => {
            if (e.key === 'Enter' || e.key === ' ') {
              document.getElementById(`${inputFileDocumentName}-documentFile`)?.click();
            }
          }}
          sx={uploader?.loading ? { pointerEvents: 'none', cursor: 'default', ...buttonStyle } : { pointerEvents: 'auto', cursor: 'pointer', ...buttonStyle }}
          variant="outlined"
          component="label"
          className="REQXX-ButtonFileUploader"
          startIcon={uploader?.appFile?.file_name ? <CloudDoneIcon /> : <CloudUploadIcon />}
          endIcon={<CircularProgress sx={uploader?.loading ? { display: 'flex' } : { display: 'none' }} size={20} color="primary" />}
          size='medium'
        >
          <p className='REQXXI-ButtonFileUploader-label'>{uploader?.appFile?.file_name ?? label}</p>
          <input
            hidden
            accept={getAllowedExtensions().accept}
            type="file"
            id={`${inputFileDocumentName}-documentFile`}
            data-testid={id}
            onClick={e => e.stopPropagation()}
            onChange={handlerOnFileUploaded}
          />
        </Button>
        <div className='REQXX-ButtonFileUploader-FileInfoWeb'>
          {disabled ? (
            <Typography color="#949494" style={{ fontSize: '10px' }}>{`${t('validating')}`}</Typography>
          ) : (
            <div style={{ alignItems: 'center', display: 'flex', justifyContent: 'space-between' }}>
              <div>
                {documentAllowedSize &&
                  <Typography color="#949494" style={{ fontSize: '10px' }}>{`(${t('maximum')} ${documentAllowedSize.toUpperCase()})`}</Typography>
                }
                <Typography color="#949494" style={{ fontSize: '10px' }}>{`(${t('allowedType')} ${getAllowedExtensions().allowed})`}</Typography>
              </div>
              <div>{validation}</div>
            </div>
          )}
        </div>
        {isInvalid &&
          <Typography className='REQXX-ButtonFileUploader-invalidmessage'>
            {invalidMessage}
          </Typography>
        }
      </Grid>
    </Grid>
  );
};

export default ButtonFileUploader;
